import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { CircleAlert, Globe, Plus, Star, X } from 'lucide-react';
import { Button, Dialog, Field, Input } from '@/components/ui';
import { idempotencyKey } from '@/utils/idempotency';
import { useSiteOperation } from '@/hooks/useSites';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { isDomainName } from '@/utils/validation';
import type { SiteDetail } from '@/types/api';

/**
 * The names of a site (S6). The whole set is edited here and sent at once:
 * which one is primary, which are aliases. The panel works out what was added
 * and removed, and says before the click what a new primary means.
 */
export function DomainsDialog({
  site,
  open,
  onClose,
}: {
  readonly site: SiteDetail;
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const initialPrimary = site.primaryDomain ?? '';
  const initialAliases = site.domains.filter((domain) => domain.role !== 'primary').map((domain) => domain.fqdn);
  const [primary, setPrimary] = useState(initialPrimary);
  const [aliases, setAliases] = useState<string[]>(initialAliases);
  const [typed, setTyped] = useState('');
  const key = useRef(idempotencyKey('site.domains'));

  const all = [primary, ...aliases];
  const www = primary.startsWith('www.') ? primary.slice(4) : `www.${primary}`;
  const changed = primary !== initialPrimary || aliases.length !== initialAliases.length || aliases.some((alias) => !initialAliases.includes(alias));
  const candidate = typed.trim().toLowerCase();
  const typedValid = isDomainName(candidate) && !all.includes(candidate);

  const save = useSiteOperation(site.id, {
    onSuccess: () => {
      toast.success('Изменение доменов поставлено в очередь');
      key.current = idempotencyKey('site.domains');
      onClose();
    },
  });

  function reset(): void {
    setPrimary(initialPrimary);
    setAliases(initialAliases);
    setTyped('');
    save.reset();
  }

  function close(): void {
    if (save.isPending) return;
    reset();
    onClose();
  }

  function add(name: string): void {
    if (!isDomainName(name) || all.includes(name)) return;
    setAliases([...aliases, name]);
    setTyped('');
  }

  function makePrimary(name: string): void {
    setAliases([...aliases.filter((alias) => alias !== name), primary]);
    setPrimary(name);
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (changed && !save.isPending) save.mutate({ command: { operation: 'domains', primaryDomain: primary, aliases }, key: key.current });
  }

  return (
    <Dialog
      open={open}
      title="Домены сайта"
      description={site.displayName}
      onClose={close}
      footer={
        <>
          <Button onClick={close} disabled={save.isPending}>
            Отмена
          </Button>
          <Button type="submit" form="site-domains" variant="primary" icon={Globe} busy={save.isPending} disabled={!changed}>
            Сохранить
          </Button>
        </>
      }
    >
      <form id="site-domains" className="stack" onSubmit={submit} noValidate>
        <ul className="domain-list">
          {all.map((name) => (
            <li key={name} className="domain-row">
              <span className="mono">{name}</span>
              {name === primary ? (
                <span className="tag">основной</span>
              ) : (
                <span className="row-tight">
                  <Button size="sm" variant="ghost" icon={Star} onClick={() => makePrimary(name)}>
                    Сделать основным
                  </Button>
                  <Button size="sm" variant="ghost" icon={X} aria-label={`Убрать ${name}`} onClick={() => setAliases(aliases.filter((alias) => alias !== name))}>
                    Убрать
                  </Button>
                </span>
              )}
            </li>
          ))}
        </ul>

        <Field label="Добавить домен" htmlFor="domain-add" hint="Домен не должен быть привязан к другому сайту">
          <div className="row-tight">
            <Input id="domain-add" value={typed} onChange={(event) => setTyped(event.target.value)} placeholder="shop.example.com" autoComplete="off" />
            <Button icon={Plus} disabled={!typedValid} onClick={() => add(candidate)}>
              Добавить
            </Button>
          </div>
        </Field>
        {primary && !all.includes(www) ? (
          <div>
            <Button size="sm" variant="ghost" icon={Plus} onClick={() => add(www)}>
              Добавить {www}
            </Button>
          </div>
        ) : null}

        <p className="field-hint">
          Для новых доменов выпускается сертификат и создаётся запись DNS.
          {primary !== initialPrimary ? ' При смене основного домена создаётся копия, адреса в базе WordPress заменяются.' : ''}
        </p>

        {save.isError ? (
          <p className="field-error" role="alert">
            <CircleAlert size={14} aria-hidden="true" />
            {errorText(save.error, 'Не удалось поставить изменение в очередь')}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
